import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faSortAlt, faUser } from '@fortawesome/pro-light-svg-icons'

import {
  Grid, StatusBadge, Text, UserProfileImage,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'

import { getColor } from '@functions/status'

import type { TaskListItemModel } from '@models/taskListItem'

import { useRelations } from '@hooks/useTaskListItem'
import { useRelations as useUserRelations } from '@hooks/useUser'

import Item from './Item'

interface OverviewProps {
  callbacks: {
    createOrEditTaskListItem: () => void,
  },
  taskListItem: TaskListItemModel,
}

const Overview = (props: OverviewProps) => {
  const { callbacks: { createOrEditTaskListItem }, taskListItem, taskListItem: { priority } } = props

  const { assignee, status } = useRelations(taskListItem)
  const { image } = useUserRelations(assignee)

  const dateKey = status.key === 'complete' ? 'complete' : 'due'
  const dateString = digObject(taskListItem, `dates.${dateKey}.date_time`, '')

  const dateLabel = status.key === 'complete' ? 'Completed Date' : 'Due Date'

  return (
    <Grid
      borderBottom="1px solid"
      borderBottomColor="lineColor"
      gridAutoRows="1fr"
      gridTemplateColumns="repeat(4, minmax(152px, 1fr))"
      gridTemplateRows="repeat(1, minmax(76px, 1fr))"
    >
      <Item callbacks={{ createOrEditTaskListItem }} icon={<FontAwesomeIcon icon={faCalendarAlt} />} label={dateLabel}>
        <Text fontSize="small">
          {dateString ? DateTime.fromFormat(dateString, 'dd/MM/yyyy h:mm').toFormat('ccc dd LLL yyyy') : 'Not Set'}
        </Text>
      </Item>

      <Item callbacks={{ createOrEditTaskListItem }} icon={<FontAwesomeIcon icon={faSortAlt} />} label="Status">
        <StatusBadge color={getColor('status', status.key || 'Pending')} text={status.title || 'Pending'} />
      </Item>

      <Item callbacks={{ createOrEditTaskListItem }} icon={<FontAwesomeIcon icon={faSortAlt} />} label="Priority">
        <StatusBadge color={getColor('priority', priority || 'default')} text={priority || 'Default'} />
      </Item>

      <Item
        callbacks={{ createOrEditTaskListItem }}
        icon={(
          <UserProfileImage boxProps={{ display: 'inline-flex' }} imageUrl={image.thumb_url}>
            <FontAwesomeIcon icon={faUser} />
          </UserProfileImage>
        )}
        label="Assignee"
      >
        <Text fontSize="small" width={106} variant="ellipsis">
          {assignee.full_name || 'Unassigned'}
        </Text>
      </Item>
    </Grid>
  )
}

export default Overview
